/**
 * The bootstrap-create race guard for users/{uid}.
 *
 * useUserDoc() is mounted by many components at once (VerifiedSignedInBlock,
 * Landing, NavDrawer, AssistantChat, FeedbackPrompt, ...). On a brand-new
 * account every instance's listener sees the same missing users/{uid}
 * snapshot, and before this module each instance issued its OWN setDoc.
 * The first write was accepted as a create; every later one was evaluated
 * as an UPDATE that changes createdAt (each carries a fresh serverTimestamp)
 * and was correctly denied by rules. The losers then showed a fatal
 * "Missing or insufficient permissions" card even though the doc was
 * created fine.
 *
 * Two pure pieces fix that, kept Firebase-free so `npm run test:handlers`
 * can drive them under --experimental-strip-types (same rule as
 * lib/availability.ts and lib/messageDrafts.ts):
 *
 *   1. ensureOnce()  - one in-flight/settled create per uid per session,
 *      shared by every hook instance. A SUCCESSFUL run stays cached forever,
 *      which also kills the offline-cache replay variant: a later
 *      exists=false snapshot from an empty local cache can never re-issue
 *      the create against a doc that already exists on the server.
 *      A FAILED run is evicted so a genuinely fresh attempt can retry
 *      later - sequentially, never in parallel.
 *
 *   2. createUserDocTolerantly() - treats permission-denied on the create
 *      as "somebody else probably won" rather than fatal: it re-checks
 *      whether the doc exists and only rethrows when the doc is genuinely
 *      absent, i.e. the rules rejected a real first create.
 */

export type EnsureRunner = () => Promise<void>;

/**
 * Issue `runner` at most once per `key` for the lifetime of `pending`.
 * Concurrent callers all await the same promise. A resolved run is cached
 * permanently (the doc exists; there is never a reason to create again).
 * A rejected run is evicted so a later caller can retry fresh.
 */
export function ensureOnce(
  pending: Map<string, Promise<void>>,
  key: string,
  runner: EnsureRunner
): Promise<void> {
  const existing = pending.get(key);
  if (existing) return existing;
  const attempt: Promise<void> = runner().catch((e: unknown) => {
    // Only evict our own entry - a newer attempt may already sit there.
    if (pending.get(key) === attempt) pending.delete(key);
    throw e;
  });
  pending.set(key, attempt);
  return attempt;
}

/** Firestore SDK permission errors carry code 'permission-denied'. */
export function isPermissionDeniedError(e: unknown): boolean {
  return (
    typeof e === 'object' &&
    e !== null &&
    (e as { code?: unknown }).code === 'permission-denied'
  );
}

export interface TolerantCreateOptions {
  /** The real create write (setDoc of the bootstrap payload). */
  create: () => Promise<void>;
  /** One fresh existence check (getDoc), used only after a denial. */
  docExists: () => Promise<boolean>;
  /** Injectable for tests; production passes isPermissionDeniedError. */
  isPermissionDenied?: (e: unknown) => boolean;
}

/**
 * Run the bootstrap create, but treat a permission-denied rejection as
 * benign when the doc turns out to exist: that denial is the signature of
 * losing the create race to another hook instance or another tab, or of an
 * offline-cache replay against a doc that was already created. Only when
 * the doc genuinely does not exist after the attempt settles does the
 * original error surface - that is a real rules rejection of a first
 * create and the user should see it.
 *
 * If the existence check itself fails (offline), the original error is
 * rethrown; the caller's live listener will deliver the doc when
 * connectivity returns and a delivered doc always outranks a stale error.
 */
export async function createUserDocTolerantly(
  opts: TolerantCreateOptions
): Promise<void> {
  const denied = opts.isPermissionDenied ?? isPermissionDeniedError;
  try {
    await opts.create();
  } catch (e) {
    if (denied(e)) {
      let exists = false;
      try {
        exists = await opts.docExists();
      } catch {
        exists = false;
      }
      if (exists) return;
    }
    throw e;
  }
}
